import {
  DEFAULT_AUTO_UPDATE_SETTINGS,
  DEFAULT_DATAROUTER_CONFIG,
  DEFAULT_FEED_SETTINGS,
  DEFAULT_GAMEPAD_SETTINGS,
  DEFAULT_KEYBOARD_SETTINGS,
  DEFAULT_NOTIFICATION_SETTINGS,
  DEFAULT_VIDEO_SETTINGS,
  RENDER_DETECTION_MODES,
} from "@observe-tech/utils";
import type { MarlinStoreDb } from "../infra/db";
import type {
  AutoUpdateSettings,
  ChartSettings,
  DatarouterConfig,
  FeedSettings,
  GamepadSettings,
  IpJsonContent,
  KeyboardSettings,
  NotificationSettings,
  RenderDetectionMode,
  VideoSettings,
  VideoSettingsPatch,
} from "../types";
import {
  assertValidAutoUpdateSettings,
  assertValidDatarouter,
  assertValidFeedSettings,
  assertValidGamepadSettings,
  assertValidIpJson,
  assertValidKeyboardSettings,
  assertValidVideoSettings,
} from "./validate";
import { assertValidNotificationSettings } from "../notifications/validate";

export {
  DEFAULT_NOTIFICATION_SETTINGS,
  DEFAULT_VIDEO_SETTINGS,
} from "@observe-tech/utils";

export const DEFAULTS = {
  datarouter: DEFAULT_DATAROUTER_CONFIG,
  autoUpdateSettings: DEFAULT_AUTO_UPDATE_SETTINGS,
  feedSettings: DEFAULT_FEED_SETTINGS,
  gamepadSettings: DEFAULT_GAMEPAD_SETTINGS,
  keyboardSettings: DEFAULT_KEYBOARD_SETTINGS,
  notificationSettings: DEFAULT_NOTIFICATION_SETTINGS,
  ipJson: null as IpJsonContent | null,
};

const getSetting = async <T>(
  db: MarlinStoreDb,
  key: string,
): Promise<T | undefined> =>
  (await db.settings.get(key))?.value as T | undefined;

const setSetting = async (
  db: MarlinStoreDb,
  key: string,
  value: unknown,
): Promise<void> => {
  await db.settings.put({ key, value });
};

const read = async <K extends keyof typeof DEFAULTS>(
  db: MarlinStoreDb,
  key: K,
): Promise<(typeof DEFAULTS)[K]> => {
  const stored = await getSetting<(typeof DEFAULTS)[K]>(db, key);
  const fallback = DEFAULTS[key];
  if (fallback === null) return (stored ?? null) as (typeof DEFAULTS)[K];
  return { ...structuredClone(fallback), ...stored } as (typeof DEFAULTS)[K];
};

export const getDatarouterConfig = (db: MarlinStoreDb) =>
  read(db, "datarouter");

export const saveDatarouterConfig = async (
  db: MarlinStoreDb,
  config: DatarouterConfig,
): Promise<{ config: DatarouterConfig; changed: boolean }> => {
  assertValidDatarouter(config);
  return db.transaction("rw", db.settings, async () => {
    const current = await read(db, "datarouter");
    const changed = (Object.keys(config) as (keyof DatarouterConfig)[]).some(
      (key) => config[key] !== current[key],
    );
    await setSetting(db, "datarouter", config);
    return { config, changed };
  });
};

export const getIpJsonContent = (db: MarlinStoreDb) => read(db, "ipJson");

export const saveIpJsonContent = async (
  db: MarlinStoreDb,
  content: IpJsonContent,
): Promise<void> => {
  assertValidIpJson(content);
  await setSetting(db, "ipJson", content);
};

export const getAutoUpdateSettings = (db: MarlinStoreDb) =>
  read(db, "autoUpdateSettings");

export const saveAutoUpdateSettings = async (
  db: MarlinStoreDb,
  settings: AutoUpdateSettings,
) => {
  assertValidAutoUpdateSettings(settings);
  await setSetting(db, "autoUpdateSettings", settings);
};

export const getFeedSettings = (db: MarlinStoreDb) => read(db, "feedSettings");

export const saveFeedSettings = async (
  db: MarlinStoreDb,
  settings: FeedSettings,
) => {
  assertValidFeedSettings(settings);
  await setSetting(db, "feedSettings", settings);
};

const LEGACY_RENDER_DETECTIONS_KEY =
  "feedProfile.pelletDetection.renderDetections";

export type LegacyVideoSettingsSources = {
  videoSettings?: Partial<VideoSettings>;
  chartSettings?: Partial<ChartSettings>;
  notificationSettings?: Record<string, unknown>;
};

export const withoutLegacyRenderDetections = (
  settings: Record<string, unknown>,
): Record<string, unknown> => {
  const { [LEGACY_RENDER_DETECTIONS_KEY]: _moved, ...rest } = settings;
  return rest;
};

export const seedVideoSettings = ({
  videoSettings: stored,
  chartSettings,
  notificationSettings,
}: LegacyVideoSettingsSources): VideoSettings | null => {
  const mode = notificationSettings?.[LEGACY_RENDER_DETECTIONS_KEY];
  const renderDetections = RENDER_DETECTION_MODES.includes(
    mode as RenderDetectionMode,
  )
    ? { defaultRenderDetections: mode as RenderDetectionMode }
    : {};

  if (!stored?.defaults) {
    const range = chartSettings?.defaultTimeRangeSeconds;
    const timeRange =
      typeof range === "number" && range > 0
        ? { defaultTimeRangeSeconds: range }
        : {};
    const carried = { ...timeRange, ...renderDetections };
    if (!stored && Object.keys(carried).length === 0) return null;
    return {
      defaults: {
        ...structuredClone(DEFAULT_VIDEO_SETTINGS.defaults),
        ...carried,
      },
      infoRowOverrides: stored?.infoRowOverrides ?? {},
    };
  }

  if (stored.defaults.defaultRenderDetections !== undefined) return null;
  return {
    defaults: { ...stored.defaults, ...renderDetections },
    infoRowOverrides: stored.infoRowOverrides ?? {},
  } as VideoSettings;
};

export const getVideoSettings = async (
  db: MarlinStoreDb,
): Promise<VideoSettings> => {
  const stored = await getSetting<Partial<VideoSettings>>(db, "videoSettings");
  return {
    defaults: {
      ...structuredClone(DEFAULT_VIDEO_SETTINGS.defaults),
      ...stored?.defaults,
    },
    infoRowOverrides: stored?.infoRowOverrides ?? {},
  };
};

export const saveVideoSettings = (
  db: MarlinStoreDb,
  patch: VideoSettingsPatch,
): Promise<VideoSettings> =>
  db.transaction("rw", db.settings, async () => {
    const current = await getVideoSettings(db);
    const rowsReplaced = patch.defaults?.defaultInfoRows !== undefined;
    const next: VideoSettings = {
      defaults: { ...current.defaults, ...patch.defaults },
      infoRowOverrides: rowsReplaced
        ? {}
        : { ...current.infoRowOverrides, ...patch.infoRowOverrides },
    };
    assertValidVideoSettings(next);
    await setSetting(db, "videoSettings", next);
    return next;
  });

export const getGamepadSettings = (db: MarlinStoreDb) =>
  read(db, "gamepadSettings");

export const saveGamepadSettings = (
  db: MarlinStoreDb,
  patch: Partial<GamepadSettings>,
): Promise<void> =>
  db.transaction("rw", db.settings, async () => {
    const next = { ...(await getGamepadSettings(db)), ...patch };
    assertValidGamepadSettings(next);
    await setSetting(db, "gamepadSettings", next);
  });

export const getKeyboardSettings = (db: MarlinStoreDb) =>
  read(db, "keyboardSettings");

export const saveKeyboardSettings = (
  db: MarlinStoreDb,
  patch: Partial<KeyboardSettings>,
): Promise<void> =>
  db.transaction("rw", db.settings, async () => {
    const next = { ...(await getKeyboardSettings(db)), ...patch };
    assertValidKeyboardSettings(next);
    await setSetting(db, "keyboardSettings", next);
  });

const isSameDay = (a: string, b: string): boolean => {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
};

export const getNotificationSettings = async (
  db: MarlinStoreDb,
): Promise<NotificationSettings> => {
  const settings = await read(db, "notificationSettings");
  if (
    settings.muteForToday &&
    (!settings.muteTimestamp ||
      !isSameDay(settings.muteTimestamp, new Date().toISOString()))
  ) {
    const { muteTimestamp: _expired, ...rest } = settings;
    return { ...rest, muteForToday: false };
  }
  return settings;
};

export const saveNotificationSettings = (
  db: MarlinStoreDb,
  patch: Partial<NotificationSettings>,
): Promise<void> =>
  db.transaction("rw", db.settings, async () => {
    const next = { ...(await getNotificationSettings(db)), ...patch };
    if (next.muteForToday && !next.muteTimestamp)
      next.muteTimestamp = new Date().toISOString();
    else if (!next.muteForToday) delete next.muteTimestamp;
    assertValidNotificationSettings(next);
    await setSetting(db, "notificationSettings", next);
  });

export const consumeReleaseNotesPrompt = (
  db: MarlinStoreDb,
  currentVersion: string,
): Promise<string | null> =>
  db.transaction("rw", db.settings, async () => {
    const lastSeen = await getSetting<string>(db, "lastSeenVersion");
    if (!lastSeen) {
      await setSetting(db, "lastSeenVersion", currentVersion);
      return null;
    }
    if (lastSeen === currentVersion) return null;
    await setSetting(db, "lastSeenVersion", currentVersion);
    return currentVersion;
  });
